import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';

type RequestStatus = 'pending' | 'reviewed' | 'completed' | 'rejected';

interface DataDeletionRequest {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  reason: string;
  message: string;
  status: RequestStatus;
  internalNotes?: string;
  reviewedAt?: string | null;
  completedAt?: string | null;
  reviewedBy?: { name: string; email: string } | null;
  metadata?: {
    source?: string;
    ipAddress?: string;
    userAgent?: string;
  };
  statusHistory?: Array<{
    status: RequestStatus;
    notes?: string;
    changedAt?: string;
    changedBy?: { name: string; email: string } | null;
  }>;
  createdAt: string;
  updatedAt: string;
}

const STATUS_COLORS: Record<RequestStatus, 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  reviewed: 'info',
  completed: 'success',
  rejected: 'error',
};

const STATUS_LABELS: Record<RequestStatus, string> = {
  pending: 'Pending',
  reviewed: 'Reviewed',
  completed: 'Completed',
  rejected: 'Rejected',
};

const fetchRequests = async (params: Record<string, any>) => {
  const res = await api.get('/admin/data-deletion-requests', { params });
  return res.data as {
    requests: DataDeletionRequest[];
    pagination: { page: number; total: number; pages: number };
  };
};

const DataDeletionRequests: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<DataDeletionRequest | null>(null);
  const [newStatus, setNewStatus] = useState<RequestStatus>('pending');
  const [internalNotes, setInternalNotes] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-data-deletion-requests', page, statusFilter, search],
    queryFn: () => fetchRequests({ page, status: statusFilter, search }),
    keepPreviousData: true,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Pick<DataDeletionRequest, 'status' | 'internalNotes'>> }) =>
      api.patch(`/admin/data-deletion-requests/${id}`, payload).then((res) => res.data.request as DataDeletionRequest),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['admin-data-deletion-requests'] });
      setSelected(updated);
      setNewStatus(updated.status);
      setInternalNotes(updated.internalNotes || '');
      toast.success('Request updated');
    },
    onError: () => toast.error('Failed to update request'),
  });

  const handleOpen = (request: DataDeletionRequest) => {
    setSelected(request);
    setNewStatus(request.status);
    setInternalNotes(request.internalNotes || '');
  };

  const handleSave = (statusOverride?: RequestStatus) => {
    if (!selected) return;
    updateMutation.mutate({
      id: selected._id,
      payload: {
        status: statusOverride || newStatus,
        internalNotes,
      },
    });
  };

  const requests = data?.requests || [];
  const pagination = data?.pagination;

  if (isLoading && !data) {
    return <LoadingSpinner message="Loading data deletion requests..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" mb={3}>
        Data Deletion / Opt-out Requests
        {pagination && (
          <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
            ({pagination.total} total)
          </Typography>
        )}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="reviewed">Reviewed</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8} md={5}>
              <TextField
                size="small"
                label="Search name, email, phone, or reason"
                value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(1); }}
                fullWidth
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {requests.length === 0 ? (
        <Alert severity="info">No data deletion or opt-out requests found.</Alert>
      ) : (
        <>
          <Stack spacing={2} mb={3}>
            {requests.map((request) => (
              <Card
                key={request._id}
                variant="outlined"
                sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                onClick={() => handleOpen(request)}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <Box flex={1} minWidth={0}>
                      <Typography variant="subtitle1" fontWeight={800} noWrap>{request.reason}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>{request.message}</Typography>
                      <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                        {request.name} - {request.phoneNumber} - {request.email} - {new Date(request.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={STATUS_LABELS[request.status]} size="small" color={STATUS_COLORS[request.status]} />
                      {request.internalNotes && <Chip label="Notes" size="small" variant="outlined" />}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {pagination && pagination.pages > 1 && (
            <Box display="flex" justifyContent="center">
              <Pagination count={pagination.pages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
            </Box>
          )}
        </>
      )}

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Box>
                <Typography variant="h6" fontWeight={800}>{selected.reason}</Typography>
                <Stack direction="row" spacing={1} mt={0.75}>
                  <Chip label={STATUS_LABELS[selected.status]} size="small" color={STATUS_COLORS[selected.status]} />
                  <Chip label={new Date(selected.createdAt).toLocaleString()} size="small" variant="outlined" />
                </Stack>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>REQUESTER</Typography>
                  <Typography variant="body2" fontWeight={700}>{selected.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{selected.phoneNumber}</Typography>
                  <Typography variant="body2" color="text.secondary">{selected.email}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>SOURCE</Typography>
                  <Typography variant="body2">{selected.metadata?.source || 'public_form'}</Typography>
                  {selected.metadata?.ipAddress && (
                    <Typography variant="body2" color="text.secondary">{selected.metadata.ipAddress}</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>MESSAGE</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{selected.message}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select value={newStatus} label="Status" onChange={(event) => setNewStatus(event.target.value as RequestStatus)}>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="reviewed">Reviewed</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Internal notes"
                    value={internalNotes}
                    onChange={(event) => setInternalNotes(event.target.value)}
                    fullWidth
                    multiline
                    minRows={4}
                    helperText="Visible to admins only. Add verification notes, action taken, or reason for rejection."
                  />
                </Grid>
                {selected.statusHistory && selected.statusHistory.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>STATUS HISTORY</Typography>
                    <Stack spacing={1} mt={1}>
                      {selected.statusHistory.slice().reverse().map((entry, index) => (
                        <Box key={`${entry.status}-${entry.changedAt || index}`} sx={{ p: 1.25, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight={700}>{STATUS_LABELS[entry.status]}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : 'No timestamp'}
                            {entry.changedBy ? ` - ${entry.changedBy.name}` : ''}
                          </Typography>
                          {entry.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{entry.notes}</Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>Close</Button>
              <Button
                variant="outlined"
                color="success"
                onClick={() => handleSave('completed')}
                disabled={updateMutation.isLoading || selected.status === 'completed'}
              >
                Mark completed
              </Button>
              <Button variant="contained" onClick={() => handleSave()} disabled={updateMutation.isLoading}>
                Save changes
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default DataDeletionRequests;
